(function (root, factory) {
  const parser = factory();
  if (typeof module === "object" && module.exports) module.exports = parser;
  if (root) root.BulkColorParser = parser;
})(typeof globalThis !== "undefined" ? globalThis : this, function () {
  "use strict";

  const DIGIT_MAP = {
    "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4",
    "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9",
    "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4",
    "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9"
  };
  const NUMBER_WORDS = new Map(Object.entries({
    one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
    uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10,
    um: 1, uma: 1, dois: 2, duas: 2,
    un: 1, une: 1, deux: 2, trois: 3, quatre: 4, cinq: 5, dix: 10,
    eins: 1, ein: 1, zwei: 2, drei: 3, vier: 4, funf: 5, fuenf: 5, zehn: 10,
    ichi: 1, ni: 2, san: 3, yon: 4, go: 5,
    hana: 1, dul: 2, set: 3,
    واحد: 1, واحدة: 1, اثنان: 2, اثنين: 2, ثلاثة: 3, اربعة: 4, أربعة: 4, خمسة: 5
  }));
  const PACKAGE_UNITS = [
    "pack", "packs", "bag", "bags", "packet", "packets", "package", "packages",
    "bolsa", "bolsas", "paquete", "paquetes", "saco", "sacos",
    "sachet", "sachets", "beutel", "packung", "packungen",
    "包", "袋", "包み", "袋分", "봉", "팩", "كيس", "اكياس", "أكياس", "عبوة", "عبوات"
  ];
  const BEAD_UNITS = [
    "bead", "beads", "pc", "pcs", "piece", "pieces",
    "perle", "perles", "cuenta", "cuentas", "conta", "contas",
    "stuck", "stueck", "stück", "颗", "顆", "粒", "个", "個", "حبة", "حبات", "خرزة", "خرزات"
  ];
  const EACH_WORDS = [
    "each", "cada", "chacun", "chacune", "chaque", "je", "pro", "each one",
    "各", "每个", "每個", "それぞれ", "各自", "각각", "كل"
  ];
  const EXCLUDE_WORDS = [
    "不要", "不用", "先不要", "不要了", "不买", "not", "skip", "exclude", "without",
    "no", "sin", "pas", "ne pas", "nicht", "ohne", "não", "nao",
    "いらない", "不要です", "除外", "제외", "빼고", "لا", "بدون"
  ];
  const RANGE_WORDS = [
    "to", "through", "thru", "hasta", "a", "ate", "até", "bis", "jusqu", "jusqu'a", "jusqu’à",
    "到", "至", "から", "まで", "에서", "까지", "الى", "إلى"
  ];
  const REMOVE_WORDS = ["remove", "delete", "discard", "不要", "不要了", "删除", "移除", "删掉",
    "quitar", "eliminar", "remover", "excluir", "supprimer", "retirer", "löschen", "entfernen",
    "削除", "外す", "제거", "삭제", "حذف", "أزل"];
  const SET_WORDS = ["set", "change to", "update to", "make it", "改成", "改为", "設為", "设为", "调整为",
    "cambiar a", "establecer", "alterar para", "definir", "mettre à", "modifier à", "ändern auf", "setzen auf",
    "に変更", "にする", "로 변경", "설정", "تغيير إلى", "اجعل"];

  const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const normalizeText = (value) =>
    String(value || "")
      .normalize("NFKC")
      .replace(/[٠-٩۰-۹]/g, (char) => DIGIT_MAP[char] || char)
      .replace(/[：]/g, ":")
      .replace(/[，、；;]/g, ",")
      .replace(/[×✕]/g, "x")
      .replace(/[–—]/g, "-")
      .replace(/\s+/g, " ")
      .trim();
  const normalizeLoose = (value) => normalizeText(value).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const normalizeColorKey = (value) => {
    const match = normalizeText(value).toUpperCase().match(/([A-Z]{1,3})[\s_-]*0*(\d{1,4})/);
    return match ? `${match[1]}${Number(match[2])}` : "";
  };
  const displayColorCode = (value) => {
    const text = normalizeText(value).toUpperCase();
    const match = text.match(/([A-Z]{1,3})[\s_-]*0*(\d{1,4})/);
    return match ? `${match[1]}${Number(match[2])}` : text;
  };
  const extractCodeCandidates = (value) => {
    const text = normalizeText(value).toUpperCase();
    const candidates = new Set();
    const pattern = /(?:^|[^A-Z0-9])([A-Z]{1,3})[\s_-]*0*(\d{1,4})(?=$|[^A-Z0-9])/g;
    let match;
    while ((match = pattern.exec(text))) candidates.add(`${match[1]}${Number(match[2])}`);
    const direct = normalizeColorKey(text);
    if (direct) candidates.add(direct);
    return [...candidates];
  };
  const parseChineseNumber = (value) => {
    const text = String(value || "").replace(/兩/g, "两");
    if (!/^[零〇一二两三四五六七八九十百]+$/.test(text)) return null;
    const digits = { 零: 0, "〇": 0, 一: 1, 二: 2, 两: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9 };
    if (text === "十") return 10;
    const tenIndex = text.indexOf("十");
    if (tenIndex >= 0) {
      const tens = tenIndex === 0 ? 1 : digits[text[tenIndex - 1]];
      const ones = tenIndex === text.length - 1 ? 0 : digits[text[tenIndex + 1]];
      return tens == null || ones == null ? null : tens * 10 + ones;
    }
    let result = "";
    for (const char of text) {
      if (digits[char] == null) return null;
      result += String(digits[char]);
    }
    return Number(result);
  };
  const parseNumberToken = (value) => {
    const token = normalizeLoose(value).trim();
    if (/^\d+$/.test(token)) return Number(token);
    if (NUMBER_WORDS.has(token)) return NUMBER_WORDS.get(token);
    return parseChineseNumber(value);
  };
  const hasAnyWord = (text, words) => {
    const loose = normalizeLoose(text);
    return words.some((word) => loose.includes(normalizeLoose(word)));
  };
  const quantityFromContext = (context, packSize) => {
    const text = normalizeText(context);
    const loose = normalizeLoose(text);
    const packageUnit = PACKAGE_UNITS.find((unit) => loose.includes(normalizeLoose(unit)));
    const beadUnit = BEAD_UNITS.find((unit) => loose.includes(normalizeLoose(unit)));
    const xMatch = text.match(/(?:x|\*|乘以?|数量|qty|quantity|qte|cant|cantidad|quantidade|menge)\s*([0-9]+|[一二两三四五六七八九十]+|[A-Za-zÀ-ÿ]+|[\u0600-\u06FF]+)/i);
    const units = [...PACKAGE_UNITS, ...BEAD_UNITS].map(escapeRegExp).join("|");
    const unitPattern = new RegExp(`([0-9]+|[一二两三四五六七八九十]+|[A-Za-zÀ-ÿ]+|[\\u0600-\\u06FF]+)\\s*(${units})`, "i");
    const decimalPattern = new RegExp(`(?:x|\\*)?\\s*\\d+[.,]\\d+\\s*(?:${units})?`, "i");
    if (decimalPattern.test(text)) {
      return { quantity: null, basis: "invalid", needsReview: true, reason: "quantityInvalid" };
    }
    const unitMatch = text.match(unitPattern);
    const bareMatch = text.match(/(?:^|[\s:])([0-9]+|[一二两三四五六七八九十]+)(?=$|[\s,])/);
    const amountToken = xMatch?.[1] ?? unitMatch?.[1] ?? bareMatch?.[1];
    if (amountToken == null) return { quantity: 1, basis: "default" };
    const amount = parseNumberToken(amountToken);
    if (amount == null || amount < 1 || !Number.isInteger(amount)) {
      return { quantity: null, basis: "invalid", needsReview: true, reason: "quantityInvalid" };
    }
    if (beadUnit && !packageUnit) return { quantity: Math.ceil(amount / packSize), basis: "beads", beadCount: amount };
    return { quantity: amount, basis: "packs" };
  };
  const splitSegments = (text) => String(text || "").normalize("NFKC").split(/\n|[;；]/).map(normalizeText).filter(Boolean);
  const codeRegexSource = "([A-Za-zＡ-Ｚａ-ｚ]{1,3})\\s*[-_]?\\s*([0-9０-９٠-٩۰-۹]{1,4})";
  const addParsedItem = (items, rawCode, quantityInfo, sourceText, flags = {}) => {
    const key = normalizeColorKey(rawCode);
    if (!key) return;
    items.push({ key, colorCode: displayColorCode(rawCode), quantity: quantityInfo.quantity, basis: quantityInfo.basis,
      beadCount: quantityInfo.beadCount, sourceText,
      needsReview: Boolean(quantityInfo.needsReview || flags.needsReview), reason: flags.reason || quantityInfo.reason || "",
      excluded: Boolean(flags.excluded) });
  };
  const parseRangeSegment = (segment, packSize) => {
    const items = [];
    const normalized = normalizeText(segment);
    const match = normalized.match(new RegExp(`${codeRegexSource}\\s*(?:-|~|${RANGE_WORDS.map(escapeRegExp).join("|")})\\s*${codeRegexSource}`, "i"));
    if (!match) return items;
    const startPrefix = match[1].toUpperCase();
    const startNumber = Number(normalizeText(match[2]));
    const endPrefix = match[3].toUpperCase();
    const endNumber = Number(normalizeText(match[4]));
    if (startPrefix !== endPrefix || !startNumber || !endNumber || endNumber < startNumber || endNumber - startNumber > 200) return items;
    const tail = normalized.slice(match.index + match[0].length);
    const quantityInfo = quantityFromContext(tail || normalized, packSize);
    for (let number = startNumber; number <= endNumber; number += 1) addParsedItem(items, `${startPrefix}${number}`, quantityInfo, segment);
    return items;
  };
  const parseListEachSegment = (segment, packSize, validKeys = null) => {
    const items = [];
    if (!hasAnyWord(segment, EACH_WORDS)) return items;
    const normalized = normalizeText(segment);
    const codePattern = new RegExp(codeRegexSource, "gi");
    const codes = [];
    let match;
    while ((match = codePattern.exec(normalized))) {
      const code = `${match[1]}${match[2]}`;
      if (!validKeys || validKeys.has(normalizeColorKey(code))) codes.push(code);
    }
    if (codes.length < 2) return items;
    const quantityInfo = quantityFromContext(normalized, packSize);
    codes.forEach((code) => addParsedItem(items, code, quantityInfo, segment));
    return items;
  };
  const parseCodeQuantitySegment = (segment, packSize, validKeys = null) => {
    const items = [];
    const normalized = normalizeText(segment);
    const excluded = hasAnyWord(normalized, EXCLUDE_WORDS);
    const codePattern = new RegExp(codeRegexSource, "gi");
    const matches = [];
    let match;
    while ((match = codePattern.exec(normalized))) {
      const key = normalizeColorKey(`${match[1]}${match[2]}`);
      if (!validKeys || validKeys.has(key)) matches.push(match);
    }
    matches.forEach((current, index) => {
      const rawCode = `${current[1]}${current[2]}`;
      const nextMatch = matches[index + 1];
      const afterEnd = nextMatch ? nextMatch.index : current.index + current[0].length + 36;
      const after = normalized.slice(current.index + current[0].length, afterEnd);
      const before = normalized.slice(Math.max(0, current.index - 18), current.index);
      const context = after.trim() ? after : index === 0 ? before : "";
      addParsedItem(items, rawCode, quantityFromContext(context, packSize), segment, { excluded, needsReview: excluded, reason: excluded ? "excluded" : "" });
    });
    return items;
  };
  const mergeItems = (items) => {
    const map = new Map();
    const review = [];
    items.forEach((item) => {
      if (item.excluded || item.needsReview || !item.quantity) return review.push(item);
      const current = map.get(item.key);
      if (current) {
        current.quantity += item.quantity;
        current.sourceText = `${current.sourceText}; ${item.sourceText}`;
      } else map.set(item.key, { ...item });
    });
    return { merged: [...map.values()], review };
  };
  const parseInput = (text, packSize = 1000, validKeys = null) => {
    const segments = splitSegments(text);
    const parsed = [];
    const consumed = new Set();
    segments.forEach((segment, index) => {
      const items = parseRangeSegment(segment, packSize);
      if (items.length) { parsed.push(...items); consumed.add(index); }
    });
    segments.forEach((segment, index) => {
      if (consumed.has(index)) return;
      const items = parseListEachSegment(segment, packSize, validKeys);
      if (items.length) { parsed.push(...items); consumed.add(index); }
    });
    segments.forEach((segment, index) => {
      if (!consumed.has(index)) parsed.push(...parseCodeQuantitySegment(segment, packSize, validKeys));
    });
    return mergeItems(parsed);
  };
  const parseAiCandidates = (payload, validKeys = null) => {
    const parsed = [];
    const addCandidate = (candidate, forceReview) => {
      const key = normalizeColorKey(candidate?.normalized_color_code || candidate?.raw_code || candidate?.normalized_code);
      if (!key || (validKeys && !validKeys.has(key))) return;
      const quantity = Number(candidate?.quantity_bags);
      const warnings = Array.isArray(candidate?.warnings) ? candidate.warnings.filter(Boolean) : [];
      const invalidQuantity = !Number.isInteger(quantity) || quantity < 1;
      parsed.push({
        key,
        colorCode: displayColorCode(candidate.normalized_color_code || candidate.raw_code || candidate.normalized_code),
        quantity: invalidQuantity ? null : quantity,
        sourceText: String(candidate.raw_text || candidate.raw_code || ""),
        needsReview: Boolean(forceReview || invalidQuantity || warnings.length),
        reason: invalidQuantity ? "quantityInvalid" : forceReview || warnings.length ? "aiNeedsReview" : "",
        excluded: false
      });
    };
    const accepted = Array.isArray(payload?.matched) ? payload.matched :
      Array.isArray(payload?.candidates) ? payload.candidates : [];
    accepted.forEach((candidate) => addCandidate(candidate, false));
    (Array.isArray(payload?.needs_review) ? payload.needs_review : []).forEach((candidate) => addCandidate(candidate, true));
    return { ...mergeItems(parsed), unparsed: Array.isArray(payload?.unparsed) ? payload.unparsed : [] };
  };
  const commandAction = (text) => hasAnyWord(text, REMOVE_WORDS) ? "remove" : hasAnyWord(text, SET_WORDS) ? "set" : "add";
  const parseCartCommands = (text, packSize = 1000, validKeys = null) => {
    const commands = [];
    const review = [];
    splitSegments(text).forEach((segment) => {
      const clauses = hasAnyWord(segment, EACH_WORDS) ? [segment] : segment.split(",").map(normalizeText).filter(Boolean);
      clauses.forEach((clause) => {
        const action = commandAction(clause);
        const parsed = parseInput(clause, packSize, validKeys);
        [...parsed.merged, ...parsed.review].forEach((item) => {
          if (action === "remove") {
            commands.push({ ...item, action, quantity: null, needsReview: false, excluded: false, reason: "" });
            return;
          }
          const needsReview = item.needsReview || (action === "set" && item.basis === "default");
          const command = { ...item, action, needsReview,
            reason: action === "set" && item.basis === "default" ? "quantityInvalid" : item.reason };
          (needsReview ? review : commands).push(command);
        });
      });
    });
    return { commands, review };
  };
  const parseAiCommands = (payload, validKeys = null) => {
    const commands = [];
    const review = [];
    const add = (candidate, forceReview) => {
      const key = normalizeColorKey(candidate?.normalized_color_code || candidate?.normalized_code || candidate?.raw_code);
      if (!key || (validKeys && !validKeys.has(key))) return;
      const action = ["add", "set", "remove"].includes(candidate?.action) ? candidate.action : "add";
      const quantity = action === "remove" ? null : Number(candidate?.quantity_bags);
      const warnings = Array.isArray(candidate?.warnings) ? candidate.warnings.filter(Boolean) : [];
      const needsReview = forceReview || warnings.length > 0 || (action !== "remove" && (!Number.isInteger(quantity) || quantity < 1));
      const item = { key, colorCode: displayColorCode(key), quantity, action,
        sourceText: String(candidate?.raw_text || candidate?.raw_code || ""), needsReview,
        reason: needsReview ? "aiNeedsReview" : "", excluded: false };
      (needsReview ? review : commands).push(item);
    };
    (Array.isArray(payload?.matched) ? payload.matched : []).forEach((item) => add(item, false));
    (Array.isArray(payload?.needs_review) ? payload.needs_review : []).forEach((item) => add(item, true));
    const intents = new Set(["cart_command", "cart_summary", "color_code_question", "quantity_calculation",
      "product_question", "policy_question", "project_advice", "clarification", "off_topic", "ambiguous"]);
    return { intent: intents.has(payload?.intent) ? payload.intent : "cart_command",
      commands, review, unparsed: Array.isArray(payload?.unparsed) ? payload.unparsed : [] };
  };
  const isPlainCartLine = (line) => (!line?.properties || Object.keys(line.properties).length === 0) &&
    !line?.selling_plan_allocation;
  const cartLinesForVariant = (cart, variantId) => (Array.isArray(cart?.items) ? cart.items : [])
    .filter((line) => String(line.variant_id) === String(variantId) && isPlainCartLine(line));
  const cartQuantityForVariant = (cart, variantId) => cartLinesForVariant(cart, variantId)
    .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
  const planCartCommands = (parsed, variantIndex, cart) => {
    const byKey = new Map();
    (parsed.commands || []).forEach((command) => {
      const variant = variantIndex.get(command.key) || null;
      if (!variant) return;
      const existing = byKey.get(command.key);
      const currentQuantity = existing ? existing.currentQuantity : cartQuantityForVariant(cart, variant.variantId);
      const previousTarget = existing ? existing.quantity : currentQuantity;
      const quantity = command.action === "remove" ? 0 : command.action === "set"
        ? Number(command.quantity) : previousTarget + Number(command.quantity || 0);
      byKey.set(command.key, { ...command, variant, currentQuantity, quantity,
        sourceText: existing ? `${existing.sourceText}; ${command.sourceText}` : command.sourceText });
    });
    const review = (parsed.review || []).map((item) => {
      const variant = variantIndex.get(item.key) || null;
      const currentQuantity = variant ? cartQuantityForVariant(cart, variant.variantId) : 0;
      return { ...item, variant, currentQuantity, quantity: currentQuantity };
    }).filter((row) => row.variant);
    return { rows: [...byKey.values(), ...review] };
  };
  const buildCartUpdates = (rows, cart) => {
    const updates = {};
    rows.forEach((row) => {
      if (!row.variant?.variantId || row.needsReview || row.quantity === row.currentQuantity) return;
      const lines = cartLinesForVariant(cart, row.variant.variantId);
      if (lines.length) {
        updates[String(lines[0].key)] = Number(row.quantity);
        lines.slice(1).forEach((line) => { updates[String(line.key)] = 0; });
      } else if (row.quantity > 0) updates[String(row.variant.variantId)] = Number(row.quantity);
    });
    return updates;
  };
  const buildCartUpdatePayload = (updates, usedAt = new Date().toISOString(), attribution = {}) => {
    const attributes = {
      "__omamori_source": "bulk_bead_order",
      "__omamori_version": "web_v1",
      "__omamori_used_at": usedAt
    };
    if (["color_list", "photo", "refill_set"].includes(attribution.inputMethod)) {
      attributes.__omamori_input_method = attribution.inputMethod;
    }
    if (attribution.inputMethod === "refill_set" && /^[a-z0-9_]+$/.test(attribution.refillSet || "")) {
      attributes.__omamori_refill_set = attribution.refillSet;
    }
    return { updates, attributes, sections: ["cart-icon-bubble", "cart-drawer", "cart-live-region-text"] };
  };
  const buildVariantIndex = (variants) => {
    const index = new Map();
    variants.forEach((variant) => {
      const candidates = new Set();
      [variant.option1, variant.option2, variant.option3, variant.sku, variant.variantTitle].forEach((value) => {
        extractCodeCandidates(value).forEach((code) => candidates.add(code));
      });
      candidates.forEach((code) => { if (!index.has(code)) index.set(code, { ...variant, colorCode: code }); });
    });
    return index;
  };

  return { buildCartUpdatePayload, buildCartUpdates, buildVariantIndex, cartQuantityForVariant, displayColorCode,
    extractCodeCandidates,
    mergeItems, normalizeColorKey, normalizeText, parseAiCandidates, parseAiCommands, parseCartCommands, parseInput,
    parseNumberToken, planCartCommands, quantityFromContext };
});
